import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useTheme } from '../../hooks/useTheme';

import { NAV_THEMES } from './nav-tokens';

interface NavMenuItemProps {
  icon: LucideIcon;
  label: string;
  /** A destination makes this a real link, so Ctrl/Cmd-click and
   *  "open in new tab" work. Omit it for rows that perform an action
   *  rather than go somewhere (a theme toggle, sign out). */
  to?: string;
  onClick?: () => void;
  tone?: 'default' | 'danger';
}

// `nb-menu-row` carries the hover in CSS — see index.css. It used to be a pair
// of mouseenter/mouseleave handlers writing an inline background, which a
// touch tap set and never cleared: the row stayed highlighted, and after a
// theme switch the stuck colour was the *previous* theme's surface, which
// swallowed the label of the very row you had just used.
const ROW =
  'nb-menu-row flex w-full items-center gap-2.5 border-0 bg-transparent px-4 py-2 text-left text-[13px] font-medium no-underline transition-colors';

/** A row in the account menu. Shared by the nav panel and the mobile nav
 *  drawer so the two can't drift apart. */
export default function NavMenuItem({
  icon: Icon,
  label,
  to,
  onClick,
  tone = 'default',
}: NavMenuItemProps) {
  const { theme } = useTheme();
  const t = NAV_THEMES[theme];
  const color = tone === 'danger' ? '#c2410c' : t.ink;
  const style = { color, '--nb-menu-hover': t.surface } as React.CSSProperties;

  const body = (
    <>
      <Icon size={14} aria-hidden />
      {label}
    </>
  );

  if (to) {
    return (
      <Link to={to} onClick={onClick} className={ROW} style={style}>
        {body}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} className={ROW} style={{ ...style, cursor: 'pointer' }}>
      {body}
    </button>
  );
}
